'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/language'
import * as DX from './exercises/deepen'

const SamplingLab = dynamic(() => import('../_components/widgets/LanguageWidgets').then(m => m.SamplingLab), { ssr: false })

export default function Sampling() {
  return (
    <Lesson>
      <Step>
        <H>From probabilities to text</H>
        <P>
          A language model doesn’t write — it outputs a probability for every possible next token. Writing means repeatedly
          picking one, appending it, and asking again. <em>How</em> you pick is called <strong>decoding</strong>, and it
          dramatically changes what comes out.
        </P>
        <ul className="gd-list">
          <li>
            <strong>Greedy</strong>: always take the single most likely token. Safe, but dull and prone to loops (“the the the”).
          </li>
          <li>
            <strong>Sampling</strong>: pick randomly, in proportion to the probabilities. Varied and natural — but occasionally
            picks something absurd from the long tail.
          </li>
        </ul>
      </Step>

      <Step gate={['q-topk', 'q-topp']}>
        <H>Three knobs</H>
        <ul className="gd-list">
          <li>
            <strong>Temperature</strong> reshapes the distribution (Module 5): low = confident and repetitive, high = adventurous
            and eventually incoherent.
          </li>
          <li>
            <strong>Top-k</strong>: only consider the k most likely tokens, then renormalise.
          </li>
          <li>
            <strong>Top-p</strong> (nucleus sampling): keep the smallest set of top tokens whose probabilities add up to at least
            p. When the model is sure, that’s just one or two tokens; when it’s unsure, many.
          </li>
        </ul>
        <Figure n="8.8" caption="A model’s scores for the next word. Faded bars are the original probabilities; solid bars are what you actually sample from after the knobs. Crossed-out words have been cut.">
          <SamplingLab />
        </Figure>
        <Quiz
          id="q-topk"
          question="With top-k = 1, what does sampling become?"
          options={[
            { label: 'Greedy decoding — only the top token survives', correct: true },
            { label: 'Fully random', why: 'With only one candidate left, there’s nothing random about it.' },
            { label: 'It depends on the temperature', why: 'Temperature rescales the probabilities, but with one candidate the pick is forced.' },
          ]}
        />
        <Quiz
          id="q-topp"
          question="Why is top-p often preferred over a fixed top-k?"
          options={[
            { label: 'It adapts: few candidates when the model is confident, more when many continuations are reasonable.', correct: true },
            { label: 'It’s faster to compute.', why: 'It needs a sort plus a running sum — similar cost. The advantage is adaptivity.' },
            { label: 'It always keeps exactly p tokens.', why: 'p is a probability mass, not a count.' },
          ]}
        />
      </Step>

      <Step gate="sampling">
        <H>Implement the decoder</H>
        <Exercise
          id="sampling"
          title="Temperature, top-k and sampling"
          {...EX.sampling}
          hints={[
            <>
              Temperature: <C>{'vec.softmax(logits.map(l => l / T))'}</C>.
            </>,
            <>
              Top-k: find the k-th largest value (sort a copy, descending, take index k − 1); zero everything smaller; divide by the
              new sum.
            </>,
            <>
              Sampling: <C>let r = random.random()</C>, then loop: <C>r -= probs[i]</C>; return <C>i</C> as soon as{' '}
              <C>{'r < 0'}</C>.
            </>,
          ]}
        />
        <Callout kind="why">
          When you set “temperature” in an AI tool, this is exactly what changes. The model itself is identical — only the way
          its probabilities are turned into words.
        </Callout>
      </Step>

      <Step gate="top-p">
        <H>Top-p: a cut-off that adapts</H>
        <P>
          Top-k always keeps exactly k tokens, which is too many when the model is sure and too few when it isn’t.{' '}
          <strong>Top-p</strong> (nucleus) sampling keeps the smallest set of tokens whose probabilities add up to p. After “The
          capital of France is”, that’s one token. After “My favourite food is”, it’s dozens.
        </P>
        <Exercise
          id="top-p"
          title="topP"
          {...DX.topP}
          hints={[
            <>
              Sort the indices by probability, biggest first: <C>{'range(probs.length).sort((a, b) => probs[b] - probs[a])'}</C>.
            </>,
            <>
              Walk that order adding to a running total and a Set of kept indices; stop once the total reaches p. Then divide the
              kept probabilities by the total and zero the rest.
            </>,
          ]}
        />
      </Step>
    </Lesson>
  )
}
